import {
  useDeleteAllFindingsMutation,
  useGetFindingsStatusCountQuery,
  useProjectId,
  usePackageId,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { FindingStatus } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { Loader2Icon, Trash2Icon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ConfirmDialog, errorToast, successToast } from '@/components';

import { FindingsFilterState, hasActiveFindingsFilter } from './types';

interface DeleteAllFindingsButtonProps {
  filters: FindingsFilterState;
}

/**
 * Button to soft-delete all findings in a package via a single backend call.
 * The backend bypasses the filterset so hidden findings (e.g. MILESTONE /
 * DELIVERABLE on RFP packages) are also deleted.
 * The count shown on the button comes from the normal GET and may be slightly
 * lower than the actual deleted count for RFP packages — the success toast
 * always shows the real number returned by the backend.
 */
export const DeleteAllFindingsButton = ({ filters }: DeleteAllFindingsButtonProps) => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [open, setOpen] = useState(false);

  const [deleteAllFindings, { isLoading: isDeleting }] = useDeleteAllFindingsMutation();

  const { processorName, blobIds, labels, search } = filters;

  // A lightweight aggregate count keeps this off the hot path — fetching every
  // finding just to size the button doesn't scale to large packages.
  const { data: statusCount, isLoading: isLoadingCount } = useGetFindingsStatusCountQuery({
    projectId,
    packageId,
    processorName,
    blobIds: blobIds.length > 0 ? blobIds : undefined,
    labels: labels.length > 0 ? labels : undefined,
    search: search.trim() || undefined,
  });

  const count = useMemo(
    () =>
      Object.entries(statusCount ?? {}).reduce(
        (total, [status, statusTotal]) =>
          status === FindingStatus.DELETED ? total : total + statusTotal,
        0,
      ),
    [statusCount],
  );

  const isLoading = isLoadingCount || isDeleting;

  const processorLabel = processorName ? formatEnumToReadableText(processorName) : null;
  const otherFiltersActive = hasActiveFindingsFilter({ ...filters, processorName: undefined });

  const buttonSuffix = processorLabel
    ? otherFiltersActive
      ? ` for filtered ${processorLabel}`
      : ` for ${processorLabel}`
    : otherFiltersActive
      ? ' for current filters'
      : '';

  const dialogSubject = processorLabel
    ? `${processorLabel} `
    : otherFiltersActive
      ? 'filtered '
      : '';

  const handleDeleteAll = useCallback(async () => {
    try {
      const result = await deleteAllFindings({
        projectId,
        packageId,
        processorName: processorName || undefined,
        blobIds: blobIds.length > 0 ? blobIds : undefined,
        labels: labels.length > 0 ? labels : undefined,
        search: search.trim() || undefined,
      }).unwrap();
      successToast({ message: `Successfully deleted ${result.deletedCount} findings` });
    } catch (error) {
      console.error('Failed to delete all findings:', error);
      errorToast({ message: 'Failed to delete findings. Please try again.' });
    } finally {
      setOpen(false);
    }
  }, [projectId, packageId, processorName, blobIds, labels, search, deleteAllFindings]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button
          variant="destructive"
          disabled={isLoading || count === 0}
          className="gap-2 max-w-xs"
          title={`Delete All Findings${buttonSuffix} (${count})`}
        >
          {isLoading ? (
            <Loader2Icon className="h-4 w-4 animate-spin shrink-0" />
          ) : (
            <Trash2Icon className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate min-w-0">Delete All Findings{buttonSuffix}</span>
          <span className="shrink-0">({count})</span>
        </Button>
      }
      title="Delete all findings?"
      description={
        <>
          This will delete all {dialogSubject}findings for this package. This action cannot be
          easily undone.
        </>
      }
      confirmLabel="Delete All"
      isLoading={isDeleting}
      onConfirm={handleDeleteAll}
    />
  );
};
